import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { useLanguage } from "../../context/LanguageContext";
import { Wordmark } from "./Wordmark";

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const LangToggle = () => {  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid="lang-switcher"
      className="flex items-center border border-black/15 bg-white/60"
      role="group"
      aria-label="Language switcher"
    >
      {["id", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green ${
            lang === l ? "bg-brand-ink text-brand-paper" : "text-black/50 hover:text-black"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "manifesto", label: t.nav.about, testid: "nav-link-manifesto" },
    { id: "services", label: t.nav.services, testid: "nav-link-services" },
    { id: "contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-brand-paper/85 backdrop-blur-xl border-b border-black/10"
          : "bg-brand-paper/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-brand-green"
          aria-label="PT Darma Daya Guna — home"
        >
          <Wordmark size="text-3xl" />
          <span className="hidden sm:block text-[10px] font-bold tracking-[0.25em] uppercase text-black/50 leading-tight">
            Darma Daya Guna
            <br />
            <span className="text-brand-green">Training &amp; Consultant</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={item.testid}
              onClick={() => scrollToId(item.id)}
              className="text-sm font-medium text-black/60 hover:text-black tracking-wide transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              {item.label}
            </button>
          ))}
          <a
            data-testid="nav-link-articles"
            href="/artikel"
            className="text-sm font-medium text-black/60 hover:text-black tracking-wide transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
          >
            {t.nav.articles}
          </a>
          <LangToggle />
          <button
            data-testid="header-cta-btn"
            onClick={() => scrollToId("contact")}
            className="group flex items-center gap-2 bg-brand-ink text-brand-paper px-5 py-2.5 text-sm font-semibold tracking-wide hover:bg-brand-green transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2"
          >
            {t.nav.cta}
            <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <div className="flex md:hidden items-center gap-3">
          <LangToggle />
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="p-2 border border-black/15 bg-white/60 focus:outline-none focus:ring-2 focus:ring-brand-green"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-brand-paper/95 backdrop-blur-xl border-b border-black/10"
            aria-label="Mobile navigation"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-${item.testid}`}
                  onClick={() => { scrollToId(item.id); setOpen(false); }}
                  className="text-left font-cabinet text-2xl font-bold text-black/80 focus:outline-none"
                >
                  {item.label}
                </button>
              ))}
              <a
                data-testid="mobile-nav-link-articles"
                href="/artikel"
                className="text-left font-cabinet text-2xl font-bold text-black/80 focus:outline-none"
              >
                {t.nav.articles}
              </a>
              <button
                data-testid="mobile-header-cta-btn"
                onClick={() => { scrollToId("contact"); setOpen(false); }}
                className="mt-2 flex items-center justify-center gap-2 bg-brand-ink text-brand-paper px-5 py-3.5 text-sm font-semibold"
              >
                {t.nav.cta}
                <ArrowUpRight size={16} weight="bold" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
